import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { motion } from 'framer-motion'
import { CheckCircle, ArrowRight } from 'lucide-react'
import { useSEO } from '../hooks/useSEO'
import { withMarketingQuery } from '../utils/marketingLinks'

// SessionStorage key to track signup completion
const SIGNUP_COMPLETED_KEY = 'optioneer_signup_completed'

export default function SignUpSuccessPage() {
  useSEO('/signup/success')
  const navigate = useNavigate()
  const location = useLocation()
  const [countdown, setCountdown] = useState(2)

  // Get redirect target from navigation state, default to trial activation
  const redirectTo = withMarketingQuery(
    (location.state as { redirectTo?: string } | null)?.redirectTo || '/trial-activation',
    location.search
  )
  const isActivationRoute = redirectTo.includes('trial-activation')
  const isPricingRoute = redirectTo.includes('pricing')

  useEffect(() => {
    // Check if user actually completed signup
    const signupCompleted = sessionStorage.getItem(SIGNUP_COMPLETED_KEY)

    if (!signupCompleted) {
      // User accessed this page directly without completing signup
      // Redirect to login page
      navigate('/signin', { replace: true })
      return
    }

    // Clear the signup completion flag
    sessionStorage.removeItem(SIGNUP_COMPLETED_KEY)

    // Start countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    // Auto-redirect to destination after 2 seconds
    const redirectTimer = setTimeout(() => {
      navigate(redirectTo, { replace: true })
    }, 2000)

    return () => {
      clearInterval(countdownInterval)
      clearTimeout(redirectTimer)
    }
  }, [navigate, redirectTo])

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-50 via-white to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950/20 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <div className="rounded-3xl bg-white dark:bg-slate-900 p-10 shadow-2xl shadow-brand-900/10 dark:shadow-black/30 border border-slate-100 dark:border-slate-800">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
            className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500 dark:from-green-500 dark:to-emerald-600 shadow-lg shadow-green-500/30"
          >
            <CheckCircle className="h-12 w-12 text-white" strokeWidth={2.5} />
          </motion.div>

          {/* Success Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-center space-y-4"
          >
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-display">
              Welcome to Optioneer! 🎉
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Your account has been created successfully.
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              {isActivationRoute || isPricingRoute
                ? "Perfect! We're redirecting you to activate your Premium trial."
                : 'We will take you to the next step.'}
            </p>
          </motion.div>

          {/* Redirect Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-50 dark:bg-slate-800 px-6 py-3 text-sm font-medium text-brand-700 dark:text-amber-400 border border-brand-200 dark:border-slate-700">
              <span>
                Redirecting {isActivationRoute || isPricingRoute ? 'to trial activation' : 'now'} in{' '}
                {countdown}s
              </span>
              <ArrowRight className="h-4 w-4 animate-pulse" />
            </div>
          </motion.div>

          {/* Manual Navigation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="mt-6 text-center"
          >
            <button
              onClick={() => navigate(redirectTo, { replace: true })}
              className="text-sm text-slate-500 dark:text-slate-400 hover:text-brand-600 dark:hover:text-amber-400 transition-colors underline underline-offset-2"
            >
              Go {isActivationRoute || isPricingRoute ? 'activate your trial' : 'continue'} now
            </button>
          </motion.div>
        </div>

        {/* Decorative Elements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="mt-6 text-center text-xs text-slate-400 dark:text-slate-600"
        >
          <p>Thank you for choosing Optioneer</p>
        </motion.div>
      </motion.div>

      {/* Background Decorations */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-brand-500/5 dark:bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-emerald-500/5 dark:bg-emerald-500/5 rounded-full blur-3xl" />
      </div>
    </div>
  )
}

// Export the sessionStorage key for use in other components
// eslint-disable-next-line react-refresh/only-export-components
export { SIGNUP_COMPLETED_KEY }
